import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Image as UserImage, MediaCrop, Video } from "../../../client/api";
import { ClavaContext } from "../../../config/contexts";
import { mapToImage, mapToVideo } from "../../../config/mediaUtils";
import { translate, TranslatorKeys } from "../../../config/translator";
import { CloudflareImage, FileType, ImagePickerReturnFile } from "../../../config/types";
import useBoolState from "../../../hooks/useBoolState";
import ClavaButton from "../ClavaButton";
import Overlay from "../Overlay";
import FileUploaderCropper from "./FileUploaderCropper";
import FileUploaderPreview from "./FileUploaderPreview";
import FileUploaderUpload from "./FileUploaderUpload";
import client from "../../../client";
import Loading from "../Loading";
import { DropzoneProvider } from "../../../hooks/useDropzone";

type FileUploaderProps<T extends FileType> = {
  as: number;
  setImg?: React.Dispatch<React.SetStateAction<UserImage | undefined>>;
  img?: UserImage;
  type: T;
  callback: (file: ImagePickerReturnFile<T>) => void;
  close: (error?: TranslatorKeys) => void;
  serverAutoCrop?: boolean;
};

type ChooseTypes = "upload" | "mediathek";
const modalRoot = document.getElementById("app") as HTMLElement;

function FileUploader<T extends FileType>({
                                            as,
                                            type,
                                            callback,
                                            close,
                                            serverAutoCrop,
                                            setImg,
                                            img
                                          }: FileUploaderProps<T>) {
  const { l, user } = useContext(ClavaContext);
  const [crop, setCrop] = useState<MediaCrop>();
  const [file, setFile] = useState<File>();
  const [blocked, toggleBlocked] = useBoolState(false);
  const [cropperHidden, setCropperHidden] = useState(false);
  const [chooseType, setChooseType] = useState<ChooseTypes>("upload");

  const [isLoadingCrop, setIsLoadingCrop] = useState<boolean>(false);
  const cropImage = useCallback(
    (input: File) => {
      if (serverAutoCrop && setImg) {
        setIsLoadingCrop(true);
        client().removeImageBackground(input).then((image: UserImage) => {
          setImg(image);
        })
          .finally(() => setIsLoadingCrop(false));
      }
    },
    [serverAutoCrop, setImg]
  );

  const onClose = useCallback(() => {
    if (!!file && type !== "pdf" && type !== "csv") {
      toggleBlocked();
    } else {
      close();
    }
  }, [toggleBlocked, close, file, type]);

  const toggleCropper = useCallback(() => {
    setCropperHidden((ch) => !ch);
  }, []);

  const selectUpload = useCallback(() => {
    setFile(undefined);
    setCropperHidden(false);
    setChooseType("upload");
  }, []);

  const style = useMemo<CSSProperties>(() => {
    const width = window.innerWidth / 2;
    const height = window.innerHeight / 2;
    if (chooseType === "upload") {
      return {
        width,
        minHeight: height
      };
    }
    return {
      width,
      minHeight: Math.min(width / as, height / 1.5)
    };
  }, [chooseType, as]);

  const onFinish = useCallback(
    (cloudflareImage?: CloudflareImage, image?: UserImage) => {
      if (image) {
        callback(image as ImagePickerReturnFile<T>);
      }
      if (crop && cloudflareImage) {
        let retval: UserImage | Video | undefined;
        if (type === "image") {
          retval = mapToImage(cloudflareImage, crop, user.id);
        }
        if (type === "video") {
          retval = mapToVideo(cloudflareImage, crop, user.id);
        }
        if (type === "media") {
          retval =
            cloudflareImage.path.indexOf("mdp") === -1
              ? mapToImage(cloudflareImage, crop, user.id)
              : mapToVideo(cloudflareImage, crop, user.id);
        }
        if (retval) {
          callback(retval as ImagePickerReturnFile<T>);
        }
      }
    },
    [crop, type, user.id, callback]
  );


  useEffect(() => {
    if ((type === "pdf" || type === "csv") && file) {
      callback(file as ImagePickerReturnFile<T>);
    }
  }, [file, type, callback]);

  const uploadTeamEmblem = useCallback(() => {
    if (file && serverAutoCrop) {
      cropImage(file);
    }
  }, [file, serverAutoCrop, cropImage]);

  return ReactDOM.createPortal(
    <DropzoneProvider>
      <div className="fixed inset-0 flex items-center justify-center text-font dark:text-font-dark">
        <Overlay close={onClose} />
        <div className="max-w-[60vw] border-orange my-2 bg-white dark:bg-bg-dark rounded-xl drop-shadow-lg">
        <div
          className="flex flex-row items-center justify-between mb-2 border-b border-b-light-gray dark:border-b-light-gray-dark">
          <span className="font-semibold text-sm p-4">
            {translate(
              type === "pdf"
                ? "selectDocument"
                : type === "image"
                  ? "selectImage"
                  : type === "video"
                    ? "selectVideo"
                    : "selectMedia",
              l
            )}
          </span>
          <button
            type="button"
            className="border-none outline-none p-4"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faClose} size="lg" />
          </button>
        </div>
        <div
          className="flex flex-row items-center justify-start mb-2 pb-2 border-b border-b-light-gray dark:border-b-light-gray-dark">
          <ClavaButton
            onClick={selectUpload}
            secondary={chooseType !== "upload" && !file}
          >
            {translate("upload", l)}
          </ClavaButton>
        </div>
        <div
          className="m-4 w-[50vw] relative flex flex-col items-stretch justify-stretch"
          style={style}
        >
          {blocked ? (
            <div className="my-8 text-center">
                              <span className="text-red text-center font-semibold">
                                {translate("sureLeave", l)}
                              </span>
              <div
                className="flex flex-row my-8 items-center justify-between"
              >
                <ClavaButton onClick={toggleBlocked}>
                  {translate("stayHere", l)}
                </ClavaButton>
                <ClavaButton onClick={close} danger>
                  {translate("close", l)}
                </ClavaButton>
              </div>
            </div>
          ) : file && type !== "pdf" ? isLoadingCrop ? (
            <Loading small />
          ) : (
            <>
              {serverAutoCrop && (
                <ClavaButton
                  onClick={uploadTeamEmblem}
                >
                  {translate("uploadTeamEmblem", l)}
                </ClavaButton>
              )}
              {!cropperHidden && (
                <FileUploaderCropper
                  file={!img ? file : undefined}
                  img={img}
                  aspectRatio={as}
                  setCrop={setCrop}
                />
              )}
              <FileUploaderPreview
                file={!img ? file : undefined}
                img={img}
                crop={crop}
                toggleCropper={toggleCropper}
                callback={onFinish}
              />
            </>
          ) : chooseType === "upload" ? (
            <FileUploaderUpload type={type} onSelect={setFile} />
          ) : null}
        </div>
      </div>
    </div>
    </DropzoneProvider>,
    modalRoot
  );
}

export default FileUploader;
